<template>
  <div class="settings-backdrop" @click.self="emit('close')">
    <div class="settings-panel">
      <div class="settings-header">
        <h2>Settings</h2>
        <button class="close-btn" @click="emit('close')" aria-label="Close" title="Close"><X :size="18" /></button>
      </div>
      <!-- Quick-actions bar: dynamically rendered from settings bar configuration -->
      <div class="settings-quick-bar" v-if="toolbarStore.settingsBarItems.length > 0">
        <template v-for="item in toolbarStore.settingsBarItems" :key="item.key">
          <!-- prev-next -->
          <template v-if="item.id === 'prev-next'">
            <button @click="emit('prev')" aria-label="Previous" title="Previous"><ChevronLeft :size="16" /></button>
            <span class="index" v-if="props.current !== undefined && props.total !== undefined">
              {{ (props.current ?? 0) + 1 }} / {{ props.total }}
            </span>
            <button @click="emit('next')" aria-label="Next" title="Next"><ChevronRight :size="16" /></button>
          </template>
          <!-- refresh -->
          <button v-else-if="item.id === 'refresh'" @click="emit('reset')" aria-label="Refresh" title="Refresh"><RefreshCw :size="16" /></button>
          <!-- tag-overlay -->
          <button v-else-if="item.id === 'tag-overlay'" @click="emit('open-tag-overlay')" aria-label="Tags" title="Tags"><Tag :size="16" /></button>
          <!-- vote-overlay -->
          <button v-else-if="item.id === 'vote-overlay'" @click="emit('open-vote-overlay')" aria-label="Vote" title="Vote"><Star :size="16" /></button>
        </template>
      </div>

      <div class="settings-body">

        <section>
          <h3>Theme</h3>
          <div class="option-selector theme-selector">
            <button :class="{ active: store.themeMode === 'light' }" @click="store.themeMode = 'light'" aria-label="Light theme" title="Light">
              <Sun :size="14" /> Light
            </button>
            <button :class="{ active: store.themeMode === 'auto' }" @click="store.themeMode = 'auto'" aria-label="Auto theme" title="Auto">
              <Monitor :size="14" /> Auto
            </button>
            <button :class="{ active: store.themeMode === 'dark' }" @click="store.themeMode = 'dark'" aria-label="Dark theme" title="Dark">
              <Moon :size="14" /> Dark
            </button>
          </div>
        </section>

        <section>
          <h3>Viewer</h3>
          <div class="viewer-mode-group">
            <span class="field-label">Mode</span>
            <div class="option-selector viewer-mode-selector">
              <button
                :class="{ active: store.viewerMode === 'single' }"
                :aria-pressed="store.viewerMode === 'single'"
                @click="store.viewerMode = 'single'"
                aria-label="Single viewer mode"
                title="Single"
              >
                <Dice1 :size="14" /> Single
              </button>
              <button
                :class="{ active: store.viewerMode === 'mobile' }"
                :aria-pressed="store.viewerMode === 'mobile'"
                @click="store.viewerMode = 'mobile'"
                aria-label="Mobile viewer mode"
                title="Mobile"
              >
                <SquareArrowDown :size="14" /> Mobile
              </button>
              <button
                :class="{ active: store.viewerMode === 'desktop' }"
                :aria-pressed="store.viewerMode === 'desktop'"
                @click="store.viewerMode = 'desktop'"
                aria-label="Desktop viewer mode"
                title="Desktop"
              >
                <SquareArrowRight :size="14" /> Desktop
              </button>
            </div>
          </div>
          <div class="viewer-mode-warning" v-if="showScrollModeWarning">
            Mobile/Desktop are still WIP scrolling modes. Bugs and wonkyness are expected.
          </div>
          <label class="toggle-row">
            <span>Zoom enabled</span>
            <input type="checkbox" v-model="store.zoomEnabled" />
          </label>
          <label class="toggle-row">
            <span>Sync zoom playback with video</span>
            <input type="checkbox" v-model="store.zoomPlaybackSync" />
          </label>
          <label class="toggle-row">
            <span>Mouse votes (double-click)</span>
            <input type="checkbox" v-model="store.mouseVotes" />
          </label>
          <label class="select-row">
            <span>Auto-archive</span>
            <select v-model="store.archivalMode" class="archival-mode-select">
              <option value="never">Never</option>
              <option value="always-archive">Always archive</option>
              <option value="always-unarchive">Always unarchive</option>
              <option value="vote-exists">Archive if voted</option>
              <option value="vote-exists-both">Archive if voted, else unarchive</option>
              <option value="positive-vote">Archive if any positive vote</option>
              <option value="positive-vote-or-unarchive">Archive if any positive vote, else unarchive</option>
              <option value="custom">Custom</option>
            </select>
          </label>
          <div v-if="store.archivalMode === 'custom'" class="custom-rules-editor">
            <VoteRuleEditor />
          </div>
          <div class="history-settings">
            <label class="toggle-row">
              <span>Reduced history steps</span>
              <input type="checkbox" v-model="store.reducedHistoryStepsEnabled" />
            </label>
            <div class="history-settings-grid">
              <label>
                Checkpoint after seconds
                <input
                  type="number"
                  min="0"
                  step="1"
                  :value="store.historyCheckpointSeconds"
                  @input="updateHistoryCheckpointSeconds"
                />
              </label>
              <label>
                Checkpoint after images
                <input
                  type="number"
                  min="1"
                  step="1"
                  :value="store.historyCheckpointImages"
                  @input="updateHistoryCheckpointImages"
                />
              </label>
            </div>
            <div class="viewer-help">
              Sequential next/previous steps share one history checkpoint until either limit is hit.
              Use <code>0</code> seconds or <code>1</code> image for immediate URL saves.
            </div>
          </div>
        </section>

        <section>
          <h3>Toolbar</h3>
          <button class="action-btn" @click="showToolbarPanel = true">
            <Settings2 :size="14" /> Customise toolbar…
          </button>
          <div class="toolbar-visibility-group">
            <span class="field-label">Top bar display</span>
            <div class="option-selector toolbar-visibility-selector">
              <button
                v-for="option in visibilityOptions"
                :key="`top-${option.value}`"
                :class="{ active: toolbarStore.topBarVisibility === option.value }"
                :aria-pressed="toolbarStore.topBarVisibility === option.value"
                :disabled="isVisibilityOptionDisabled(option.value)"
                @click="setTopBarVisibility(option.value)"
              >
                {{ option.label }}
              </button>
            </div>
            <div
              v-if="topBarModeNote !== null"
              class="toolbar-mode-note"
            >
              {{ topBarModeNote }}
            </div>
            <div
              v-if="topBarFallbackWarning !== null"
              class="toolbar-warning"
            >
              {{ topBarFallbackWarning }}
            </div>
            <div
              v-else-if="!isSingleViewer && toolbarStore.topBarVisibility === 'hover'"
              class="toolbar-warning"
            >
              Hover-only can be awkward on a scrolling page.
            </div>
          </div>
          <div class="toolbar-visibility-group">
            <span class="field-label">Bottom bar display</span>
            <div class="option-selector toolbar-visibility-selector">
              <button
                v-for="option in visibilityOptions"
                :key="`bottom-${option.value}`"
                :class="{ active: toolbarStore.bottomBarVisibility === option.value }"
                :aria-pressed="toolbarStore.bottomBarVisibility === option.value"
                :disabled="isVisibilityOptionDisabled(option.value)"
                @click="setBottomBarVisibility(option.value)"
              >
                {{ option.label }}
              </button>
            </div>
            <div
              v-if="bottomBarModeNote !== null"
              class="toolbar-mode-note"
            >
              {{ bottomBarModeNote }}
            </div>
            <div
              v-if="bottomBarFallbackWarning !== null"
              class="toolbar-warning"
            >
              {{ bottomBarFallbackWarning }}
            </div>
            <div
              v-else-if="!isSingleViewer && toolbarStore.bottomBarVisibility === 'hover'"
              class="toolbar-warning"
            >
              Hover-only can be awkward on a scrolling page.
            </div>
          </div>
        </section>

        <section>
          <h3>Saved searches</h3>
          <button v-if="!isAddingNew && editingId === null" class="action-btn" @click="isAddingNew = true" style="margin-bottom: 8px;">
            <Plus :size="14" /> Add new…
          </button>
          <div v-if="isAddingNew" class="saved-edit" style="margin-bottom: 12px;">
            <input v-model="editName" placeholder="Name" />
            <input v-model="editAlias" placeholder="Alias" style="width:4em;" />
            <textarea v-model="editTags" placeholder="tag1, tag2" rows="2" />
            <span class="saved-btns">
              <button @click="commitNewSearch()" aria-label="Save"><Check :size="13" /></button>
              <button @click="isAddingNew = false" aria-label="Cancel"><X :size="13" /></button>
            </span>
          </div>
          <div v-if="savedSearches.searches.length === 0 && !isAddingNew" class="empty-note">No saved searches yet. Use the ▼ button in the search bar.</div>
          <ul class="saved-list">
            <li v-for="s in savedSearches.searches" :key="s.id" class="saved-item">
              <div v-if="editingId !== s.id" class="saved-view">
                <span class="saved-name">
                  <span v-if="s.alias" class="alias-badge">{{ s.alias }}</span>
                  {{ s.name }}
                </span>
                <code class="saved-tags">{{ s.tags.join(", ") }}</code>
                <span class="saved-btns">
                  <button @click="startEdit(s)" aria-label="Edit" title="Edit"><Pencil :size="13" /></button>
                  <button @click="savedSearches.remove(s.id)" aria-label="Delete" title="Delete" class="delete-btn"><Trash2 :size="13" /></button>
                </span>
              </div>
              <div v-else class="saved-edit">
                <input v-model="editName" placeholder="Name" />
                <input v-model="editAlias" placeholder="Alias" style="width:4em;" />
                <textarea v-model="editTags" placeholder="tag1, tag2" rows="2" />
                <span class="saved-btns">
                  <button @click="commitEdit(s.id)" aria-label="Save"><Check :size="13" /></button>
                  <button @click="editingId = null" aria-label="Cancel"><X :size="13" /></button>
                </span>
              </div>
            </li>
          </ul>
        </section>

        <section>
          <h3>Favorite tags</h3>
          <div class="fav-chips" v-if="favoriteTags.tags.length > 0">
            <span v-for="tag in favoriteTags.tags" :key="tag" class="fav-chip">
              {{ tag }}
              <button @click="favoriteTags.remove(tag)" aria-label="Remove" title="Remove favorite"><X :size="10" /></button>
            </span>
          </div>
          <div class="empty-note" v-else>No favorite tags. Use the ★ button on any tag.</div>
          <label class="toggle-row" style="margin-top: 8px;">
            Max recent tags
            <input
              type="number"
              min="0"
              step="1"
              :value="recentTagsStore.maxTags"
              @input="updateMaxRecentTags"
              style="width: 4em;"
            />
          </label>
        </section>

        <section>
          <h3>Connection</h3>
          <label>Host URL:</label>
          <div class="host-input-wrapper">
            <input type="text" v-model="hostDraft" />
            <RecentHostsDropdown @select="hostDraft = $event" />
          </div>
          <button class="action-btn" type="button" @click="saveHost()">
            Save &amp; reconnect
          </button>
          <label class="toggle-row">
            <span>Enable auto-retry on network errors</span>
            <input type="checkbox" v-model="store.retryAutoEnabled" />
          </label>
          <div v-if="store.retryAutoEnabled" class="retry-settings">
            <label>
              Max retry attempts:
              <input
                type="number"
                v-model.number="store.retryMaxAttempts"
                min="1"
                max="50"
              />
              <span class="help-text">(1-50, default: 15)</span>
            </label>
            <label>
              Initial retry delay (ms):
              <input
                type="number"
                v-model.number="store.retryInitialDelay"
                min="100"
                max="10000"
                step="100"
              />
              <span class="help-text">(100-10000ms, default: 2000ms)</span>
            </label>
            <p class="info-text">
              When a network error occurs, the app will automatically retry with exponential backoff.
              Each retry waits longer than the previous one (up to 60 seconds).
            </p>
          </div>
        </section>

        <section>
          <h3>Services</h3>
          <div class="select-row">
            <span>Vote services</span>
            <button class="action-btn" @click="showVoteServicePanel = true">
              <Star :size="14" /> Configure…
            </button>
          </div>
          <div class="select-row">
            <span>Refresh services</span>
            <button class="action-btn" @click="refreshServices" :disabled="store.isLoadingAvailableServices">
              <RefreshCw :size="14" :class="{ spinning: store.isLoadingAvailableServices }" />
              Refresh
            </button>
          </div>
          <div v-if="tagReadServices === null" class="services-section">
            <label>
              Tag read service ID:
              <input type="text" v-model="store.tagReadServiceId" placeholder="Load services to use dropdown" />
            </label>
            <label>
              Tag write service ID:
              <input type="text" v-model="store.tagWriteServiceId" placeholder="Load services to use dropdown" />
            </label>
          </div>
          <div v-else class="services-section">
            <label>
              Tag read service:
              <select v-model="store.tagReadServiceId">
                <option v-for="service in tagReadServices" :key="service.service_key" :value="service.service_key">
                  {{ service.name }}
                </option>
              </select>
            </label>
            <label>
              Tag write service:
              <select v-model="store.tagWriteServiceId">
                <option v-for="service in tagWriteServices" :key="service.service_key" :value="service.service_key">
                  {{ service.name }}
                </option>
              </select>
            </label>
          </div>
        </section>

      </div>
    </div>
  </div>
  <ToolbarCustomizationPanel v-if="showToolbarPanel" @close="showToolbarPanel = false" />
  <VoteServiceConfigPanel v-if="showVoteServicePanel" @close="showVoteServicePanel = false" />
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useApiStore } from "@/stores/api";
import { Check, ChevronLeft, ChevronRight, Dice1, Moon, Monitor, Pencil, Plus, RefreshCw, Settings2, SquareArrowDown, SquareArrowRight, Star, Sun, Tag, Trash2, X } from "lucide-vue-next";
import ToolbarCustomizationPanel from "@/components/ToolbarCustomizationPanel.vue";
import { useSavedSearchesStore } from "@/stores/savedSearches";
import { useFavoriteTagsStore } from "@/stores/favoriteTags";
import { useRecentTagsStore } from "@/stores/recentTags";
import { type BarVisibility, isBarVisibilityDisabled, resolveBarVisibility, useToolbarStore } from "@/stores/toolbar";
import VoteRuleEditor from "@/components/rules/VoteRuleEditor.vue";
import VoteServiceConfigPanel from "@/components/rules/VoteServiceConfigPanel.vue";
import RecentHostsDropdown from "@/components/RecentHostsDropdown.vue";
import { useRecentHostsStore } from "@/stores/recentHosts";
import { API_HOST, setLocalStorageItem } from "@/stores/localStorage";


const favoriteTags = useFavoriteTagsStore();
const recentTagsStore = useRecentTagsStore();
const toolbarStore = useToolbarStore();

const showToolbarPanel = ref(false);
const showVoteServicePanel = ref(false);
const savedSearches = useSavedSearchesStore();

const editingId = ref<string | null>(null);
const isAddingNew = ref(false);
const editName = ref("");
const editAlias = ref("");
const editTags = ref("");

function startEdit(s: { id: string; name: string; alias?: string; tags: string[] }) {
  editingId.value = s.id;
  editName.value = s.name;
  editAlias.value = s.alias ?? "";
  editTags.value = s.tags.join(", ");
}

function commitEdit(id: string) {
  savedSearches.update(id, {
    name: editName.value.trim(),
    alias: editAlias.value.trim() || undefined,
    tags: editTags.value.split(",").map((t) => t.trim()).filter(Boolean),
  });
  editingId.value = null;
}

function commitNewSearch() {
  if (!editName.value.trim()) return;
  savedSearches.storeCurrentSearch(
    editName.value.trim(),
    editTags.value.split(",").map((t) => t.trim()).filter(Boolean),
    editAlias.value.trim() || undefined
  );
  editName.value = "";
  editAlias.value = "";
  editTags.value = "";
  isAddingNew.value = false;
}

const props = defineProps<{
  current?: number;
  total?: number;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "prev"): void;
  (e: "next"): void;
  (e: "reset"): void;
  (e: "open-tag-overlay"): void;
  (e: "open-vote-overlay"): void;
}>();
const store = useApiStore();
const recentHostsStore = useRecentHostsStore();
const hostDraft = ref<string>(store.host ?? "");

async function saveHost() {
  store.host = hostDraft.value;
  setLocalStorageItem(API_HOST, hostDraft.value);
  recentHostsStore.addHost(hostDraft.value);
  await store.ensureSession();
}

const isSingleViewer = computed(() => store.viewerMode === "single");
const showScrollModeWarning = computed(() => store.viewerMode === "mobile" || store.viewerMode === "desktop");
const visibilityOptions = [
  { value: "always", label: "In image list" },
  { value: "fixed", label: "Fixed to Window" },
  { value: "autohide", label: "Show on scroll" },
  { value: "hover", label: "Hover only" },
] as const;

const topBarEffectiveVisibility = computed(() =>
  resolveBarVisibility(store.viewerMode, toolbarStore.topBarVisibility)
);
const bottomBarEffectiveVisibility = computed(() =>
  resolveBarVisibility(store.viewerMode, toolbarStore.bottomBarVisibility)
);

function isVisibilityOptionDisabled(value: BarVisibility): boolean {
  return isBarVisibilityDisabled(store.viewerMode, value);
}

function setTopBarVisibility(value: BarVisibility) {
  if (isVisibilityOptionDisabled(value)) return;
  toolbarStore.topBarVisibility = value;
}

function setBottomBarVisibility(value: BarVisibility) {
  if (isVisibilityOptionDisabled(value)) return;
  toolbarStore.bottomBarVisibility = value;
}

function updateHistoryCheckpointSeconds(event: Event) {
  const target = event.target as HTMLInputElement | null;
  if (!target) return;
  const nextValue = target.valueAsNumber;
  store.historyCheckpointSeconds = Number.isFinite(nextValue) ? nextValue : 0;
  store.reducedHistoryStepsEnabled = true;
}

function updateHistoryCheckpointImages(event: Event) {
  const target = event.target as HTMLInputElement | null;
  if (!target) return;
  const nextValue = target.valueAsNumber;
  store.historyCheckpointImages = Number.isFinite(nextValue) ? nextValue : 1;
  store.reducedHistoryStepsEnabled = true;
}

function updateMaxRecentTags(event: Event) {
  const target = event.target as HTMLInputElement | null;
  if (!target) return;
  const nextValue = target.valueAsNumber;
  recentTagsStore.maxTags = Number.isFinite(nextValue) && nextValue >= 0 ? Math.floor(nextValue) : 5;
}

function describeBarModeNote(position: "top" | "bottom", visibility: BarVisibility): string | null {
  if (visibility === "always") {
    return `The ${position} bar stays attached to the scrolling image list.`;
  }
  if (visibility === "autohide") {
    return position === "top"
      ? "The top bar slides in while scrolling up and recedes while scrolling down."
      : "The bottom bar slides in while scrolling down and recedes while scrolling up.";
  }
  if (visibility === "hover") {
    return `The ${position} bar appears on desktop hover, or by tapping the screen edge on touch devices.`;
  }
  if (visibility === "fixed") {
    return `The ${position} bar is pinned to the viewport and detached from the scrolling list.`;
  }
  return null;
}

const topBarModeNote = computed(() =>
  describeBarModeNote("top", topBarEffectiveVisibility.value)
);
const bottomBarModeNote = computed(() =>
  describeBarModeNote("bottom", bottomBarEffectiveVisibility.value)
);

const topBarFallbackWarning = computed(() => {
  if (toolbarStore.topBarVisibility === "always" && topBarEffectiveVisibility.value === "fixed") {
    return "In image list falls back to Fixed to Window in single.";
  }
  if (toolbarStore.topBarVisibility === "autohide" && topBarEffectiveVisibility.value === "fixed") {
    if (isSingleViewer.value) {
      return "Show on scroll is only meaningful in the scrolling viewers and falls back to Fixed to Window in single.";
    }
    if (store.viewerMode === "desktop") {
      return "Show on scroll is unavailable in desktop sideways mode and falls back to Fixed to Window.";
    }
  }
  return null;
});

const bottomBarFallbackWarning = computed(() => {
  if (toolbarStore.bottomBarVisibility === "always" && bottomBarEffectiveVisibility.value === "fixed") {
    return "In image list falls back to Fixed to Window in single.";
  }
  if (toolbarStore.bottomBarVisibility === "autohide" && bottomBarEffectiveVisibility.value === "fixed") {
    if (isSingleViewer.value) {
      return "Show on scroll is only meaningful in the scrolling viewers and falls back to Fixed to Window in single.";
    }
    if (store.viewerMode === "desktop") {
      return "Show on scroll is unavailable in desktop sideways mode and falls back to Fixed to Window.";
    }
  }
  return null;
});

// Services computed properties
const tagReadServices = computed(() =>
  store.availableServices
    ? Object.values(store.availableServices).filter((s) => s.type === 5 || s.type === 10)
    : null
);

const tagWriteServices = computed(() =>
  store.availableServices
    ? Object.values(store.availableServices).filter((s) => s.type === 5)
    : null
);

async function refreshServices(): Promise<void> {
  await store.refreshAvailableServices();
}

function isTyping(e: KeyboardEvent): boolean {
  const target = e.target as HTMLElement | null;
  if (!target) return false;
  const tag = target.tagName?.toLowerCase() ?? "";
  return tag === "input" || tag === "textarea" || tag === "select" || target.isContentEditable;
}

function onKeyDown(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && e.key === ",") {
    emit("close");
    e.preventDefault();
    e.stopImmediatePropagation();
  } else if (e.key === "s" && !e.metaKey && !e.ctrlKey && !e.altKey && !isTyping(e)) {
    emit("close");
    e.preventDefault();
    e.stopImmediatePropagation();
  }
}

onMounted(() => document.addEventListener("keydown", onKeyDown));
onUnmounted(() => document.removeEventListener("keydown", onKeyDown));
</script>

<style scoped lang="scss">
.settings-backdrop {
  position: fixed;
  inset: 0;
  background: var(--bg-overlay);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.settings-panel {
  background: var(--bg-surface);
  color: var(--text-primary);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  width: min(480px, 95vw);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Mobile: slide up as a bottom-sheet */
@media (max-width: 640px) {
  .settings-backdrop {
    align-items: flex-end;
  }

  .settings-panel {
    width: 100vw;
    max-height: 92dvh;
    border-radius: var(--radius-lg) var(--radius-lg) 0 0;
    border-bottom: none;
  }
}

.settings-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--border);

  h2 {
    margin: 0;
    font-size: 1.1rem;
  }

  .close-btn {
    background: none;
    border: none;
    color: var(--text-primary);
    font-size: 1.5rem;
    cursor: pointer;
    line-height: 1;

    &:hover { color: var(--accent); }
  }
}

.settings-quick-bar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--border);

  button {
    background: none;
    border: none;
    color: var(--text-primary);
    cursor: pointer;
    padding: 4px 6px;
    border-radius: var(--radius-sm);
    display: flex;
    align-items: center;
    &:hover { color: var(--accent); background: var(--btn-hover-bg); }
  }

  .index {
    padding: 0 8px;
    font-size: 0.85rem;
    color: var(--text-secondary, var(--text-primary));
    opacity: 0.8;
  }
}

.settings-body {
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  section {
    h3 {
      margin: 0 0 8px;
      font-size: 0.85rem;
      text-transform: uppercase;
      color: var(--text-accent);
    }
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 0.9rem;

    input[type="text"], select, textarea {
      background: var(--bg-elevated);
      border: 1px solid var(--border);
      border-radius: var(--radius-sm);
      color: var(--text-primary);
      padding: 6px 8px;
      font-size: 0.9rem;
      width: 100%;
      box-sizing: border-box;
    }
  }

  .toggle-row {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .select-row {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 8px;

    select {
      flex-shrink: 0;
      max-width: 200px;
    }
  }

  .empty-note {
    font-size: 0.8rem;
    opacity: 0.6;
  }

  .saved-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .saved-item {
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 8px;
  }

  .saved-view {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .saved-name {
    font-size: 0.85rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .alias-badge {
    background: var(--accent);
    color: #fff;
    border-radius: 3px;
    padding: 0 4px;
    font-size: 0.75em;
  }

  .saved-tags {
    font-size: 0.75rem;
    opacity: 0.7;
    word-break: break-all;
  }

  .saved-btns {
    display: flex;
    gap: 4px;
    margin-top: 4px;

    button {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--text-primary);
      padding: 2px 4px;
      border-radius: var(--radius-sm);
      opacity: 0.6;
      &:hover { opacity: 1; color: var(--accent); }
      &.delete-btn:hover { color: #e55; }
    }
  }

  .saved-edit {
    display: flex;
    flex-direction: column;
    gap: 5px;

    input, textarea {
      background: var(--bg-surface);
      border: 1px solid var(--border);
      border-radius: var(--radius-sm);
      color: var(--text-primary);
      padding: 4px 6px;
      font-size: 0.85rem;
      width: 100%;
      box-sizing: border-box;
    }
  }

  .fav-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .fav-chip {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 3px 8px;
    font-size: 0.8rem;

    button {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--text-primary);
      padding: 0;
      display: flex;
      opacity: 0.6;
      &:hover { opacity: 1; color: #e55; }
    }
  }

  .action-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 12px;
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    color: var(--text-primary);
    cursor: pointer;
    font-size: 0.85rem;
    &:hover:not(:disabled) { border-color: var(--accent); color: var(--accent); }
    &:disabled { opacity: 0.6; cursor: not-allowed; }

    svg.spinning {
      animation: spin 1s linear infinite;
    }
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .toolbar-visibility-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .toolbar-mode-note,
  .toolbar-warning {
    font-size: 0.8rem;
    line-height: 1.5;
  }

  .toolbar-mode-note {
    opacity: 0.8;
  }

  .toolbar-warning {
    color: var(--text-primary);
    border: 1px solid color-mix(in srgb, var(--accent) 25%, var(--border));
    background: color-mix(in srgb, var(--accent) 10%, var(--bg-surface));
    border-radius: var(--radius-sm);
    padding: 8px 10px;
  }

  .field-label {
    font-size: 0.9rem;
  }

  .option-selector {
    display: flex;
    flex-wrap: nowrap;
    gap: 0;
    width: 100%;

    button {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      padding: 8px 4px;
      background: var(--bg-elevated);
      border: 1px solid var(--border);
      border-radius: 0;
      color: var(--text-primary);
      cursor: pointer;
      font-size: 0.85rem;
      line-height: 1.2;
      white-space: nowrap;
      transition: background var(--transition-fast), border-color var(--transition-fast);
      margin-left: -1px;

      &:first-child {
        margin-left: 0;
        border-radius: var(--radius-sm) 0 0 var(--radius-sm);
      }

      &:last-child {
        border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
      }

      &:hover:not(:disabled) { border-color: var(--accent); }
      &:disabled {
        cursor: not-allowed;
        opacity: 0.45;
        background: color-mix(in srgb, var(--bg-elevated) 75%, var(--bg-surface));
        border-color: var(--border);
        color: var(--text-secondary, var(--text-primary));
      }
      &.active {
        background: var(--accent);
        color: #fff;
        border-color: var(--accent);
      }
    }
  }

  .viewer-mode-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .history-settings {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 12px;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    background: var(--bg-elevated);
  }

  .history-settings-grid {
    display: grid;
    gap: 8px;
    grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));

    label {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    input[type="number"] {
      background: var(--bg-surface);
      border: 1px solid var(--border);
      border-radius: var(--radius-sm);
      color: var(--text-primary);
      padding: 6px 8px;
      font-size: 0.9rem;
      width: 100%;
      box-sizing: border-box;
    }
  }

  .viewer-help {
    font-size: 0.8rem;
    line-height: 1.5;
    opacity: 0.8;
  }

  .toolbar-visibility-selector {
    margin-top: 6px;

    button {
      min-height: 3.1rem;
      padding: 8px 6px;
      white-space: normal;
      text-align: center;
      font-size: 0.8rem;
      letter-spacing: 0.01em;
    }
  }

  .viewer-mode-warning {
    font-size: 0.8rem;
    line-height: 1.5;
    padding: 8px 10px;
    border-radius: var(--radius-sm);
    border: 1px solid color-mix(in srgb, var(--accent) 35%, var(--border));
    background: color-mix(in srgb, var(--accent) 10%, var(--bg-elevated));
    color: var(--text-primary);
  }

  @media (max-width: 640px) {
    .toolbar-visibility-selector button {
      min-height: 3rem;
      padding: 8px 5px;
    }
  }

  .services-section {
    display: flex;
    flex-direction: column;
    gap: 8px;

    label {
      display: flex;
      flex-direction: column;
      gap: 4px;

      input[type="text"],
      select {
        background: var(--bg-surface);
        border: 1px solid var(--border);
        border-radius: var(--radius-sm);
        color: var(--text-primary);
        padding: 6px 8px;
        font-size: 0.9rem;
        width: 100%;
        box-sizing: border-box;
      }
    }
  }

  .host-input-wrapper {
    display: flex;
    gap: 6px;
    align-items: center;
    input { flex: 1; }
  }

  .retry-settings {
    margin-top: 8px;
    padding: 12px;
    background: var(--bg-elevated);
    border-left: 3px solid var(--accent);
    border-radius: var(--radius-sm);
    display: flex;
    flex-direction: column;
    gap: 8px;

    label {
      display: flex;
      flex-direction: column;
      gap: 4px;

      input[type="number"] {
        background: var(--bg-surface);
        border: 1px solid var(--border);
        border-radius: var(--radius-sm);
        color: var(--text-primary);
        padding: 6px 8px;
        font-size: 0.9rem;
        width: 100%;
        box-sizing: border-box;
      }

      .help-text {
        font-size: 0.75rem;
        color: var(--text-secondary, var(--text-primary));
        opacity: 0.7;
      }
    }

    .info-text {
      font-size: 0.8rem;
      color: var(--text-secondary, var(--text-primary));
      opacity: 0.8;
      line-height: 1.4;
      margin: 4px 0 0 0;
    }
  }
}
</style>
